import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  MenuItem,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { catalogosService } from '../../services/catalogosService';
import { CreateUnidadMedidaRequest, UpdateUnidadMedidaRequest } from '../../types';

const UnidadMedidaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { idUnidadMedida } = useParams<{ idUnidadMedida: string }>();
  const isNew = !idUnidadMedida || idUnidadMedida === 'nuevo';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [idField, setIdField] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [cantDecimales, setCantDecimales] = useState<string>('0');
  const [usaBalanza, setUsaBalanza] = useState('NO');
  const [porcToleranciaPick, setPorcToleranciaPick] = useState<string>('');

  const load = useCallback(async () => {
    if (isNew || !idUnidadMedida) return;
    const id = decodeURIComponent(idUnidadMedida);
    try {
      setLoading(true);
      const u = await catalogosService.getUnidadMedida(id);
      setIdField(u.idUnidadMedida);
      setDescripcion(u.descripcion || '');
      setCantDecimales(u.cantDecimales != null ? String(u.cantDecimales) : '0');
      setUsaBalanza((u.usaBalanza || 'NO').trim().toUpperCase().slice(0, 2) || 'NO');
      setPorcToleranciaPick(
        u.porcToleranciaPick != null ? String(u.porcToleranciaPick) : ''
      );
    } catch (e) {
      enqueueSnackbar('Error al cargar la unidad de medida', { variant: 'error' });
      navigate('/inventario/unidades-medida');
    } finally {
      setLoading(false);
    }
  }, [idUnidadMedida, isNew, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (isNew) {
      setIdField('');
      setDescripcion('');
      setCantDecimales('0');
      setUsaBalanza('NO');
      setPorcToleranciaPick('');
      return;
    }
    load();
  }, [isNew, idUnidadMedida, load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const descTrim = descripcion.trim();
    if (!descTrim) {
      enqueueSnackbar('La descripción es obligatoria', { variant: 'warning' });
      return;
    }
    if (descTrim.length > 20) {
      enqueueSnackbar('La descripción admite como máximo 20 caracteres', { variant: 'warning' });
      return;
    }

    const cantNum = Math.min(255, Math.max(0, parseInt(cantDecimales, 10) || 0));
    const porcRaw = porcToleranciaPick.trim();
    const porcNum = porcRaw === '' ? null : parseFloat(porcRaw.replace(',', '.'));

    if (isNew) {
      const idTrim = idField.trim();
      if (!idTrim) {
        enqueueSnackbar('El código es obligatorio', { variant: 'warning' });
        return;
      }
      if (idTrim.length > 20) {
        enqueueSnackbar('El código admite como máximo 20 caracteres', { variant: 'warning' });
        return;
      }
    }

    try {
      setSaving(true);
      if (isNew) {
        const idTrim = idField.trim();
        const body: CreateUnidadMedidaRequest = {
          idUnidadMedida: idTrim,
          descripcion: descTrim,
          cantDecimales: cantNum,
          usaBalanza: usaBalanza.trim() || 'NO',
          porcToleranciaPick: porcNum != null && !Number.isNaN(porcNum) ? porcNum : null,
        };
        await catalogosService.createUnidadMedida(body);
      } else {
        const body: UpdateUnidadMedidaRequest = {
          descripcion: descTrim,
          cantDecimales: cantNum,
          usaBalanza: usaBalanza.trim() || 'NO',
          porcToleranciaPick: porcNum != null && !Number.isNaN(porcNum) ? porcNum : null,
        };
        await catalogosService.updateUnidadMedida(idField, body);
      }
      enqueueSnackbar('Guardado correctamente', { variant: 'success' });
      navigate('/inventario/unidades-medida');
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        'Error al guardar';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#f5f5f5',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {isNew ? 'Nueva unidad de medida' : 'Editar unidad de medida'}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Código"
                  value={idField}
                  onChange={(e) => setIdField(e.target.value.toUpperCase())}
                  fullWidth
                  required
                  disabled={!isNew}
                  inputProps={{ maxLength: 20 }}
                  helperText="Máx. 20 caracteres (char en base de datos)"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Descripción"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  fullWidth
                  required
                  inputProps={{ maxLength: 20 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Cantidad de decimales"
                  type="number"
                  value={cantDecimales}
                  onChange={(e) => setCantDecimales(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0, max: 255 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Usa balanza"
                  value={usaBalanza}
                  onChange={(e) => setUsaBalanza(e.target.value)}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="NO">NO</MenuItem>
                  <MenuItem value="SI">SI</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="% tolerancia pick"
                  type="number"
                  value={porcToleranciaPick}
                  onChange={(e) => setPorcToleranciaPick(e.target.value)}
                  fullWidth
                  inputProps={{ step: '0.01', min: 0 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/inventario/unidades-medida')}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none' }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UnidadMedidaForm;
